import { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Phone, Shield, User, Lock, Heart, Stethoscope, Truck, Calendar, Users, ArrowLeft } from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import authService from "../../services/authService";
import OtpInput from "../common/OtpInput";
import CountdownTimer from "../common/CountdownTimer";

export default function Signup() {
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [canResendOTP, setCanResendOTP] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1); // 1 = Details, 2 = OTP Verification

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};

    if (!mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^\d{10}$/.test(mobile.replace(/\D/g, ""))) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number";
    }

    if (!name.trim()) {
      newErrors.name = "Full name is required";
    } else if (name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    if (!password.trim()) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    try {
      const cleanMobile = mobile.replace(/\s/g, '');
      const response = await authService.sendOTP(cleanMobile);

      // API returns: { "message": "OTP sent successfully" }
      if (response.message) {
        setOtpSent(true);
        setStep(2);
        setCanResendOTP(false);
        setErrors({ success: response.message });
      }
    } catch (error) {
      console.error("Send OTP error:", error);
      // API returns: { "error": "Mobile is required" } or other error
      setErrors({ general: error.error || error.message || "Failed to send OTP. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setErrors({});

    try {
      const cleanMobile = mobile.replace(/\s/g, '');
      const response = await authService.sendOTP(cleanMobile);

      // API returns: { "message": "OTP sent successfully" }
      if (response.message) {
        setCanResendOTP(false);
        setErrors({ success: response.message });
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      // API returns: { "error": "..." }
      setErrors({ general: error.error || error.message || "Failed to resend OTP. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndSignup = async (e) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      setErrors({ general: "Please enter a valid 6-digit OTP" });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const cleanMobile = mobile.replace(/\s/g, '');

      // Call signup API with OTP
      const response = await authService.signup({
        mobile: cleanMobile,
        name: name.trim(),
        password: password,
        otp: otp,
      });

      // API returns: { "message": "Registered successfully", "tokens": { "refresh": "...", "access": "..." } }
      if (response.tokens) {
        // Auto-login after successful registration
        login({
          access: response.tokens.access,
          refresh: response.tokens.refresh,
          user: { mobile: cleanMobile, username: name.trim() }
        });
        navigate("/");
      }
    } catch (error) {
      console.error("Signup error:", error);
      // API returns: { "error": "User with this mobile already exists" } or { "error": "Invalid or expired OTP" }
      setErrors({ general: error.error || error.message || "Registration failed. Please check your OTP and try again." });
    } finally {
      setLoading(false);
    }
  };

  const formatMobileNumber = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{5})(\d{5})/, "$1 $2");
    }
    return numbers.slice(0, 10).replace(/(\d{5})(\d{5})/, "$1 $2");
  };

  const handleMobileChange = (e) => {
    const formatted = formatMobileNumber(e.target.value);
    setMobile(formatted);
    if (errors.mobile) {
      setErrors(prev => ({ ...prev, mobile: "" }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex">
      {/* Left Side - Information Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-green-600 p-12 flex-col justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-32 h-32 rounded-full bg-white"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 rounded-full bg-white"></div>
          <div className="absolute top-1/2 left-1/3 w-16 h-16 rounded-full bg-white"></div>
        </div>
        
        <div className="relative z-10 text-white">
          <Link to="/" className="flex items-center mb-8 hover:opacity-90 transition-opacity">
            <Heart className="w-10 h-10 mr-3 text-red-300" />
            <h1 className="text-4xl font-bold">Home Cure Express</h1>
          </Link>
          
          <h2 className="text-2xl font-semibold mb-6 leading-tight">
            Join Our Healthcare Family
          </h2>
          
          <p className="text-lg mb-12 text-blue-100 leading-relaxed">
            Your complete home-based healthcare provider. From medicine ordering to paramedical services, 
            we make wellness convenient and accessible.
          </p>
          
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <Stethoscope className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Order Medicines Online</h3>
                <p className="text-blue-100">Get prescribed medicines delivered to your doorstep</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <Heart className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Medical Services at Home</h3>
                <p className="text-blue-100">Professional healthcare services in your comfort zone</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <Truck className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">24/7 Emergency Support</h3>
                <p className="text-blue-100">Round-the-clock medical assistance when you need it</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Trusted by 10,000+ Families</h3>
                <p className="text-blue-100">Join our growing community of satisfied customers</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Signup Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Header */}
          <div className="lg:hidden mb-8">
            <button onClick={() => navigate('/')} className="flex items-center text-gray-600 hover:text-gray-800 mb-4 transition-colors">
              <ArrowLeft className="w-5 h-5 mr-1" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <div className="text-center">
              <Link to="/" className="inline-flex items-center justify-center hover:opacity-80 transition-opacity">
                <Heart className="w-8 h-8 mr-2 text-blue-600" />
                <h1 className="text-2xl font-bold text-gray-800">Home Cure Express</h1>
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="hidden lg:flex items-center justify-between mb-6">
              <button onClick={() => navigate('/')} className="flex items-center text-gray-500 hover:text-gray-700 transition-colors">
                <ArrowLeft className="w-4 h-4 mr-1" />
                <span className="text-sm font-medium">Back</span>
              </button>
            </div>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Create Your Account</h2>
              <p className="text-gray-600">Join Home Cure Express for convenient healthcare services</p>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                }`}>
                  1
                </div>
                <div className={`w-16 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                }`}>
                  2
                </div>
              </div>
            </div>

            {errors.general && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{errors.general}</p>
              </div>
            )}

            {errors.success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-600 text-sm">{errors.success}</p>
              </div>
            )}

            {/* Step 1: User Details */}
            {step === 1 && (
            <div className="space-y-6">
              {/* Mobile Number Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type="tel"
                    placeholder="Enter mobile number"
                    value={mobile}
                    onChange={handleMobileChange}
                    className={`auth-input w-full pl-14 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                      errors.mobile
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    maxLength="11"
                  />
                </div>
                {errors.mobile && (
                  <p className="mt-2 text-sm text-red-600">{errors.mobile}</p>
                )}
              </div>

              {/* Full Name Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Enter full name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (errors.name) setErrors(prev => ({ ...prev, name: "" }));
                    }}
                    className={`auth-input w-full pl-14 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                      errors.name
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                </div>
                {errors.name && (
                  <p className="mt-2 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type="password"
                    placeholder="Create password (min 6 chars)"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password) setErrors(prev => ({ ...prev, password: "" }));
                    }}
                    className={`auth-input w-full pl-14 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                      errors.password
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type="password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: "" }));
                    }}
                    className={`auth-input w-full pl-14 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                      errors.confirmPassword
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="mt-2 text-sm text-red-600">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Send OTP Button */}
              <button
                onClick={handleSendOTP}
                disabled={loading}
                className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending OTP...</span>
                  </div>
                ) : (
                  'Send OTP'
                )}
              </button>

              {/* Login Link */}
              <div className="text-center text-gray-600 text-sm">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
                >
                  Sign In
                </Link>
              </div>
            </div>
            )}

            {/* Step 2: OTP Verification */}
            {step === 2 && (
            <div className="space-y-6">
              {/* OTP Info */}
              <div className="text-center">
                <p className="text-gray-600 mb-2">
                  We've sent a 6-digit OTP to
                </p>
                <p className="text-lg font-semibold text-gray-800">{mobile}</p>
                <button
                  onClick={() => setStep(1)}
                  className="text-blue-600 hover:text-blue-700 text-sm mt-2"
                >
                  Change Number
                </button>
              </div>

              {/* OTP Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                  Enter OTP
                </label>
                <OtpInput
                  value={otp}
                  onChange={setOtp}
                  length={6}
                />
              </div>

              {/* Resend OTP */}
              <div className="text-center">
                {canResendOTP ? (
                  <button
                    onClick={handleResendOTP}
                    disabled={loading}
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                  >
                    Resend OTP
                  </button>
                ) : (
                  <div className="text-gray-600 text-sm">
                    Resend OTP in{' '}
                    <CountdownTimer
                      seconds={30}
                      onComplete={() => setCanResendOTP(true)}
                    />
                  </div>
                )}
              </div>

              {/* Verify Button */}
              <button
                onClick={handleVerifyAndSignup}
                disabled={loading || otp.length !== 6}
                className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 ${
                  loading || otp.length !== 6
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Verifying...</span>
                  </div>
                ) : (
                  'Verify & Create Account'
                )}
              </button>

              {/* Back Button */}
              <button
                onClick={() => {
                  setStep(1);
                  setOtp("");
                  setErrors({});
                }}
                className="w-full text-gray-600 hover:text-gray-800 text-sm"
              >
                ← Back to Details
              </button>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}